/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Table } from 'antd'
import classNames from 'classnames'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { PerEmpty } from '../../../components/custom_components/Empty'
import PerText from '../../../components/custom_components/Text'
import { PyramidType } from '../../../types'
import { usePyramidsStore } from '../PyramidsStore'
import { PyramidsData } from '../pyramidUtils'
import { LimitType } from './PyramidLimitType.enum'
import './PyramidsTable.less'

interface Props {
  tableData: PyramidsData[]
  type: PyramidType
  variant: 'pyramidsView' | 'companySettings' | 'companySettingsEditable'
  modalOpen?: (isOpen: boolean) => void
  className?: string
  canEdit?: boolean
}

export const PyramidsTable: FC<Props> = ({ tableData, type, variant, modalOpen, className, canEdit = false }) => {
  const [t] = useTranslation()
  const pyramidsView = variant === 'pyramidsView'
  const companySettings = variant === 'companySettings'
  const companySettingsEditable = variant === 'companySettingsEditable'
  const showRecommended = pyramidsView && tableData.some((data) => data.recommended)
  const pastExists = pyramidsView && tableData.some((data) => data.past)
  const forecastExists = pyramidsView && tableData.some((data) => data.forecast !== undefined && data.name !== 'total')
  const currentNumberOfEmployees = tableData.find((data) => data.name === 'total')!
  const [columnWidth, setColumnWidth] = useState(0)
  const [columnQuarterWidth, setColumnQuarterWidth] = useState(0)
  const [columnEighthWidth, setColumnEighthWidth] = useState(0)

  if (companySettings) {
    tableData = tableData.filter(({ name }) => name !== 'total')
  }

  const [showPast, showForecast] = usePyramidsStore((state) => [state.showPast, state.showForecast], shallow)

  const calcVariance = (value: number, recommended: number, numberOfEmployees: number, limitType: string) => {
    const nValue = Math.round(numberOfEmployees * (value / 100))
    const nRecommended = Math.round(numberOfEmployees * (recommended / 100))

    if (limitType === LimitType.Min) {
      const result = nValue > nRecommended ? `+${nValue - nRecommended}` : nValue - nRecommended
      return (
        <span className={classNames({ 'recommended-value': nValue >= nRecommended, 'out-of-bounds-value': nValue < nRecommended })}>
          {result}
        </span>
      )
    }

    const result = nRecommended > nValue ? `+${nRecommended - nValue}` : nRecommended - nValue
    return (
      <span className={classNames({ 'out-of-bounds-value': nValue > nRecommended, 'recommended-value': nValue <= nRecommended })}>
        {result}
      </span>
    )
  }

  const columns = [
    {
      key: 'pyramids-range',
      title: <span style={{ fontWeight: !!pyramidsView ? 'bold' : 'inherit' }}>{t(`pyramidsView.type.${type}`)}</span>,
      className: pyramidsView ? 'column' : 'textAlign-left padding-right--2-percent',
      width: !!pyramidsView ? `${columnWidth}%` : `${columnWidth + 4 * columnEighthWidth}%`,
      render: ({ name }: PyramidsData) => {
        if (pyramidsView) {
          return type === PyramidType.Level
            ? t(`pyramidsView.shared.overview.level.${name}`)
            : name === 'total'
            ? t(`pyramidsView.shared.overview.level.${name}`)
            : name
        } else {
          return type === PyramidType.Level ? t(`employee.contract.level.${name}`) : name
        }
      },
    },
    {
      key: 'pyramids-recommended',
      title: <span style={{ fontWeight: !!pyramidsView ? 'bold' : 'inherit' }}>{t(`pyramidsView.shared.overview.recommended`)}</span>,
      className: companySettings ? 'textAlign-left noPadding-left noPadding-right' : 'column',
      width: companySettingsEditable
        ? !!pyramidsView
          ? `${columnWidth + 2 * columnQuarterWidth}%`
          : `${columnWidth + 3 * columnEighthWidth}%`
        : undefined,
      render: ({ recommended, limitType }: PyramidsData) => {
        const recommended_absolute_value = recommended ? Math.round((currentNumberOfEmployees.current! * recommended) / 100) : 0

        return typeof recommended === 'number' ? (
          <PerText>
            <PerText fontSize="s" className="gray noMargin-right" display="inline-block" mr="xxs">
              {t(`pyramidsView.shared.overview.${limitType}`)}
            </PerText>
            {`${recommended_absolute_value} (${recommended}%)`}
          </PerText>
        ) : null
      },
    },
    {
      key: 'pyramids-past',
      title: (
        <span style={{ fontWeight: !!pyramidsView ? 'bold' : 'inherit' }}>
          {t(`pyramidsView.shared.overview.${pyramidsView ? 'past' : 'heading'}`)}
        </span>
      ),
      className: companySettings ? 'textAlign-right' : 'column--past',
      width: companySettingsEditable ? `${columnWidth}%` : undefined,
      render: ({ past, name }: PyramidsData) => {
        return typeof past === 'number' ? (
          <PerText fontWeight="bold">
            {name !== 'total' && <>{Math.round(currentNumberOfEmployees.past! * (past / 100)) + ` (${past}%)`}</>}
            {name === 'total' && <span style={{ fontWeight: 'normal' }}>{`${past} emp.`}</span>}
          </PerText>
        ) : null
      },
    },
    {
      key: 'pyramids-current',
      title: (
        <span style={{ fontWeight: !!pyramidsView ? 'bold' : 'inherit' }}>
          {t(`pyramidsView.shared.overview.${pyramidsView ? 'current' : 'heading'}`)}
        </span>
      ),
      className: companySettings ? 'textAlign-right' : 'column--current',
      width: companySettingsEditable ? `${columnWidth}%` : undefined,
      render: ({ current, name }: PyramidsData) => {
        return typeof current === 'number' ? (
          <PerText fontWeight="bold">
            {name !== 'total' && <>{Math.round(currentNumberOfEmployees.current! * (current / 100)) + ` (${current}%)`}</>}
            {name === 'total' && <span style={{ fontWeight: 'normal' }}>{`${current} emp.`}</span>}
          </PerText>
        ) : null
      },
    },
    {
      key: 'pyramids-current-variance',
      title: <span style={{ fontWeight: !!pyramidsView ? 'bold' : 'inherit' }}>{t(`pyramidsView.shared.overview.variance`)}</span>,
      className: companySettings ? 'textAlign-right' : 'column--current',
      width: companySettingsEditable ? `${3 * columnQuarterWidth}%` : undefined,
      render: ({ current, recommended, limitType, name }: PyramidsData) => {
        return typeof current === 'number' ? (
          <PerText fontWeight="bold">
            {name !== 'total' && <>{calcVariance(current, recommended || 0, currentNumberOfEmployees.current!, limitType || '')}</>}
          </PerText>
        ) : null
      },
    },
    {
      key: 'pyramids-forecast',
      title: (
        <span style={{ fontWeight: !!pyramidsView ? 'bold' : 'inherit' }}>
          {t(`pyramidsView.shared.overview.${pyramidsView ? 'forecast' : 'heading'}`)}
        </span>
      ),
      className: companySettings ? 'textAlign-right' : 'column--forecast',
      width: companySettingsEditable ? `${columnWidth}%` : undefined,
      render: ({ forecast, name }: PyramidsData) => {
        return typeof forecast === 'number' ? (
          <PerText fontWeight="bold">
            {name !== 'total' && <>{Math.round(currentNumberOfEmployees.forecast! * (forecast / 100)) + ` (${forecast}%)`}</>}
            {name === 'total' && <span style={{ fontWeight: 'normal' }}>{`${forecast} emp.`}</span>}
          </PerText>
        ) : null
      },
    },
    {
      key: 'pyramids-forecast-variance',
      title: <span style={{ fontWeight: !!pyramidsView ? 'bold' : 'inherit' }}>{t('pyramidsView.shared.overview.variance')}</span>,
      className: companySettings ? 'textAlign-right' : 'column--forecast',
      width: companySettingsEditable ? `${3 * columnQuarterWidth}%` : undefined,
      render: ({ forecast, recommended, limitType, name }: PyramidsData) => {
        return typeof forecast === 'number' ? (
          <PerText fontWeight="bold">
            {name !== 'total' && <>{calcVariance(forecast, recommended || 0, currentNumberOfEmployees.forecast!, limitType || '')}</>}
          </PerText>
        ) : null
      },
    },
    {
      key: 'pyramids-actions',
      title: (
        <PerButtonCircle
          variant="pastel"
          color="primary"
          icon="edit_fill"
          size="small"
          tooltip={t('tooltip.edit')}
          onClick={() => modalOpen && modalOpen(true)}
        />
      ),
      className: 'textAlign-center padding-left--5-px padding-right--5-px',
      width: !!pyramidsView ? undefined : `${columnEighthWidth}%`,
    },
  ]

  const filterColumns = (column: { key: string }) => {
    if (companySettings && canEdit && ['pyramids-past', 'pyramids-current', 'pyramids-forecast'].includes(column.key)) {
      return false
    }
    if (pyramidsView && !showRecommended && column.key === 'pyramids-recommended') {
      return false
    }
    if ((!showPast || !pastExists) && column.key === 'pyramids-past') {
      return false
    }
    if ((!showForecast || !forecastExists) && column.key === 'pyramids-forecast') {
      return false
    }
    if ((!pyramidsView || !showRecommended) && column.key === 'pyramids-current-variance') {
      return false
    }
    if ((!showForecast || !showRecommended || !forecastExists) && column.key === 'pyramids-forecast-variance') {
      return false
    }
    return (companySettings && canEdit) || column.key !== 'pyramids-actions'
  }

  useEffect(() => {
    let count = 0
    for (const col of columns) {
      if (filterColumns({ key: col.key })) {
        count += 1
      }
    }
    setColumnWidth(parseFloat((100 / count).toFixed(2)))
    setColumnQuarterWidth(parseFloat((100 / (4 * count)).toFixed(2)))
    setColumnEighthWidth(parseFloat((100 / (8 * count)).toFixed(2)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns])

  return (
    <Table
      className={(pyramidsView || companySettings ? 'light noRadius' : undefined) + ' ' + className + (pyramidsView ? ' table' : undefined)}
      dataSource={tableData.map((pyramidEntry) => ({ ...pyramidEntry, key: pyramidEntry.name }))}
      columns={columns.filter(filterColumns)}
      pagination={false}
      locale={{
        emptyText: <PerEmpty heading={t('table.emptyText')} icon />,
      }}
    />
  )
}
